/**
 * Geo + language detection helpers.
 *
 * The marketing site sits behind Cloudflare. Cloudflare exposes the visitor's
 * resolved country + region via `/cdn-cgi/trace`, a tiny text endpoint that
 * returns key=value lines. We fetch it once per session, cache the result in
 * sessionStorage, and use it to (1) auto-default Quebec visitors to French
 * and (2) gate the QuebecLanguageBanner so it only fires for visitors who
 * actually look Quebec-shaped.
 *
 * Cheap, no third-party API, no PII stored. The trace endpoint returns the
 * already-resolved fields Cloudflare uses for routing — same data they expose
 * via request headers on the edge.
 */

export interface GeoSignal {
  country: string | null; // ISO-2, e.g. "CA"
  region: string | null;  // ISO subdivision, e.g. "QC" for Quebec
  source: "cloudflare-trace" | "navigator" | "unknown";
}

const SESSION_KEY = "cr-geo-signal";

/**
 * Fetch the Cloudflare trace once and cache it in sessionStorage. Subsequent
 * calls in the same session return the cached value without a network round
 * trip.
 */
export async function detectGeo(): Promise<GeoSignal> {
  // SSR / non-browser safety
  if (typeof window === "undefined") {
    return { country: null, region: null, source: "unknown" };
  }

  // Cache hit
  const cached = sessionStorage.getItem(SESSION_KEY);
  if (cached) {
    try {
      return JSON.parse(cached) as GeoSignal;
    } catch {
      // fall through to fresh fetch
    }
  }

  // Cloudflare trace: returns text like "fl=...\nip=...\nloc=CA\ncolo=YUL\n..."
  // We only need `loc` (country) and `cf-ipregion` if present. Region is not
  // always in /cdn-cgi/trace — when missing we still cache the country so
  // navigator.language can be the secondary signal.
  let signal: GeoSignal = { country: null, region: null, source: "unknown" };

  try {
    const resp = await fetch("/cdn-cgi/trace", {
      cache: "no-store",
      // 1.5s budget — we don't want to delay first paint waiting for this
      signal: AbortSignal.timeout(1500),
    });
    if (resp.ok) {
      const text = await resp.text();
      const map = new Map<string, string>();
      for (const line of text.split("\n")) {
        const idx = line.indexOf("=");
        if (idx > 0) map.set(line.slice(0, idx).trim(), line.slice(idx + 1).trim());
      }
      signal = {
        country: map.get("loc") || null,
        // `cf-ipregion` is not in default trace but ZeroTrust / paid plans
        // sometimes expose it. We try and fall back to null cleanly.
        region: map.get("cf-ipregion") || null,
        source: "cloudflare-trace",
      };
    }
  } catch {
    // Network blocked, AbortSignal fired, or trace endpoint not enabled.
    // Fall through to navigator-only signal.
  }

  // Navigator fallback (always populated, even when CF unavailable)
  if (!signal.country) {
    const lang = navigator.language || "";
    // fr-CA / en-CA / etc — extract region after the dash
    const parts = lang.split("-");
    if (parts.length === 2) {
      signal = { country: parts[1].toUpperCase(), region: null, source: "navigator" };
    }
  }

  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(signal));
  } catch {
    // sessionStorage can throw in private mode / quota exhausted — non-fatal
  }

  return signal;
}

/**
 * Quick synchronous check: does the visitor's navigator.language indicate
 * French preference? Used as a fallback when /cdn-cgi/trace hasn't returned
 * yet or when region detection is unavailable.
 */
export function navigatorPrefersFrench(): boolean {
  if (typeof navigator === "undefined") return false;
  const lang = (navigator.language || "").toLowerCase();
  if (lang.startsWith("fr")) return true;
  // Some browsers populate languages[] with the broader preference list
  const langs = navigator.languages || [];
  return langs.some((l) => l.toLowerCase().startsWith("fr"));
}

/**
 * Combined "should this visitor probably see French?" check. Used to gate the
 * QuebecLanguageBanner. Conservative: only triggers when we have a strong
 * signal (CA + QC, or fr-* navigator language, or Cloudflare loc=CA + fr-*
 * navigator).
 */
export async function shouldOfferFrench(): Promise<boolean> {
  const navFr = navigatorPrefersFrench();
  const geo = await detectGeo();

  // Strongest signal: CF says CA + QC explicitly
  if (geo.country === "CA" && geo.region === "QC") return true;

  // CF says CA + browser is fr-* — high confidence Quebecker on EN device,
  // or French Canadian anywhere in CA
  if (geo.country === "CA" && navFr) return true;

  // Navigator alone is fr-CA — explicit Quebec locale
  if (typeof navigator !== "undefined") {
    const lang = (navigator.language || "").toLowerCase();
    if (lang === "fr-ca") return true;
  }

  // Fall back to navigator fr-* if CF gave us nothing
  if (geo.source !== "cloudflare-trace" && navFr) return true;

  return false;
}
